import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { CheckCircle, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollSubscriptionStatus(sessionId);
    }
  }, [searchParams]);

  const pollSubscriptionStatus = async (sessionId) => {
    if (attempts >= 5) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === "paid") {
        setStatus("success");
        await refreshUser();
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          pollSubscriptionStatus(sessionId);
        }, 2000);
      }
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Navbar />

      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        {status === "loading" && (
          <div>
            <div className="spinner mx-auto mb-6"></div>
            <h1 className="font-['Cormorant_Garamond'] text-3xl font-light text-[#1A1B1A]">
              Activating your subscription...
            </h1>
            <p className="text-[#5C5D5C] mt-4">Please wait while we confirm your payment.</p>
          </div>
        )}

        {status === "success" && (
          <div className="animate-fade-in" data-testid="subscription-success">
            <div className="w-20 h-20 bg-[#3E6656]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-[#3E6656]" />
            </div>
            <h1 className="font-['Cormorant_Garamond'] text-4xl font-light text-[#1A1B1A]">
              Welcome to Premium!
            </h1>
            <p className="text-[#5C5D5C] mt-4 max-w-md mx-auto">
              Your subscription is now active. Enjoy unlimited AI room visualisations and premium features.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Button
                onClick={() => navigate("/ai-visualizer")}
                className="bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none px-8 py-4"
                data-testid="start-visualizing-btn"
              >
                <Sparkles size={18} className="mr-2" /> Start Visualising
              </Button>
              <Button
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="border-[#20C997] text-[#20C997] rounded-none px-8 py-4"
              >
                Go to Dashboard <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          </div>
        )}

        {status === "expired" && (
          <div className="animate-fade-in">
            <h1 className="font-['Cormorant_Garamond'] text-3xl font-light text-[#9E4747]">
              Payment Session Expired
            </h1>
            <p className="text-[#5C5D5C] mt-4">Your payment session has expired. Please try again.</p>
            <Button
              onClick={() => navigate("/subscription")}
              className="mt-6 bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none"
            >
              Try Again
            </Button>
          </div>
        )}

        {(status === "error" || status === "timeout") && (
          <div className="animate-fade-in">
            <h1 className="font-['Cormorant_Garamond'] text-3xl font-light text-[#9E4747]">
              Something went wrong
            </h1>
            <p className="text-[#5C5D5C] mt-4">
              We couldn't confirm your subscription. Please check your email or contact support.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="mt-6 bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none"
            >
              Go to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
